import { logger } from '@/lib/logger';

import type { CurrentUserDto, PermissionDto } from '../client';

/**
 * View model for the authenticated user. Used by `useMe()` and any UI that
 * needs to render the current user + permission checks.
 *
 * Stability contract:
 *   - Field renames from snake_case → camelCase are explicit below. No
 *     generic deep transform. If the contract adds / renames a field, the
 *     mapper must be updated deliberately so the rename shows up in code
 *     review.
 *   - `role` is narrowed to a known union with `'UNKNOWN'` as the fallback;
 *     the backend promises the enum via OpenAPI, but we defensively guard
 *     against future additions.
 *   - `permissionKeys` is a flat `Set<'resource:ACTION'>` for O(1) lookups
 *     at the point of use (`hasPermission('patient', 'READ')`).
 */

export type UserRole = 'GENETIC_COUNSELOR' | 'PHYSICIAN' | 'NURSE' | 'ADMIN' | 'UNKNOWN';
/**
 * Account lifecycle status (PTV2-1424, DESIGN §9.2). Drives the `SessionGuard`
 * state-page switch: non-ACTIVE users are blocked at authentication (the
 * backend refuses every non-`/me` path with a 403 `account_*` code) and the FE
 * renders a branded pending / deactivated dead-end instead of the app.
 * Absent / unrecognised on the wire → treated as `ACTIVE` so an older backend
 * that predates the field never locks anyone out.
 */
export type AccountStatus = 'ACTIVE' | 'PENDING_ACTIVATION' | 'DEACTIVATED';
/** Matches BFF vocabulary plus PhenoTips-style verbs from live `/me`. */
export type PermissionAction =
  | 'READ'
  | 'WRITE'
  | 'DELETE'
  | 'APPROVE'
  | 'CREATE'
  | 'UPDATE'
  | 'MANAGE';

export interface UserPermission {
  readonly resource: string;
  readonly actions: readonly PermissionAction[];
}

export interface UserProfile {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly fullName: string;
  readonly role: UserRole;
  readonly status: AccountStatus;
  readonly department: string | null;
  readonly title: string | null;
  readonly permissions: readonly UserPermission[];
  readonly permissionKeys: ReadonlySet<string>;
}

const KNOWN_STATUSES: ReadonlySet<AccountStatus> = new Set<AccountStatus>([
  'ACTIVE',
  'PENDING_ACTIVATION',
  'DEACTIVATED',
]);

function narrowStatus(value: unknown): AccountStatus {
  if (typeof value === 'string') {
    for (const status of KNOWN_STATUSES) {
      if (status === value) {
        return status;
      }
    }
  }
  // Missing / unrecognised → ACTIVE, so an older backend that predates the
  // field (or a stub principal) is never locked out of the app.
  return 'ACTIVE';
}

const KNOWN_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'GENETIC_COUNSELOR',
  'PHYSICIAN',
  'NURSE',
  'ADMIN',
]);

const KNOWN_ACTIONS: ReadonlySet<PermissionAction> = new Set<PermissionAction>([
  'READ',
  'WRITE',
  'DELETE',
  'APPROVE',
  'CREATE',
  'UPDATE',
  'MANAGE',
]);

function narrowRole(value: string): UserRole {
  for (const role of KNOWN_ROLES) {
    if (role === value) {
      return role;
    }
  }
  return 'UNKNOWN';
}

function narrowAction(value: string): PermissionAction | null {
  for (const action of KNOWN_ACTIONS) {
    if (action === value) {
      return action;
    }
  }
  return null;
}

function mapPermission(dto: PermissionDto): UserPermission | null {
  if (typeof dto.resource !== 'string' || dto.resource.length === 0) return null;
  const actions: PermissionAction[] = [];
  for (const raw of dto.actions ?? []) {
    const action = narrowAction(raw);
    if (action !== null) actions.push(action);
  }
  return { resource: dto.resource, actions };
}

function buildPermissionKeys(permissions: readonly UserPermission[]): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const perm of permissions) {
    for (const action of perm.actions) {
      keys.add(`${perm.resource}:${action}`);
    }
  }
  return keys;
}

function nullableString(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function composeFullName(first: string, last: string): string {
  return [first, last].map((part) => part.trim()).filter((part) => part.length > 0).join(' ');
}

/**
 * Schema-tolerant mapping of `GET /me`.
 *
 * The OpenAPI contract marks `guid`, `email`, `first_name`, `last_name`, and
 * `role` as required, but in practice a misconfigured BE (auth disabled,
 * stub principal, partial migration) can ship a payload that's missing or
 * mistypes them. Rather than throwing — which lands the user on the
 * "You're signed out" screen via `RealAuthProvider.deriveStatus` — we
 * substitute safe defaults and emit a single `me.schema.invalid` warning
 * listing every offending field. The warning routes through `logger.warn`
 * → `console.warn`, so the BE owner sees exactly what to fix in devtools.
 */
export function mapCurrentUser(dto: CurrentUserDto): UserProfile {
  const raw = dto as unknown as Record<string, unknown>;
  const issues: string[] = [];

  const requireString = (field: string, fallback: string): string => {
    const value = raw[field];
    if (typeof value === 'string' && value.length > 0) return value;
    issues.push(`${field}: expected non-empty string, got ${describeType(value)}`);
    return fallback;
  };

  const guid = requireString('guid', '(unknown)');
  const email = requireString('email', '');
  const firstName = requireString('first_name', '');
  const lastName = requireString('last_name', '');

  let role: UserRole = 'UNKNOWN';
  const roleRaw = raw.role;
  if (typeof roleRaw === 'string' && roleRaw.length > 0) {
    role = narrowRole(roleRaw);
  } else {
    issues.push(`role: expected non-empty string, got ${describeType(roleRaw)}`);
  }

  const permissions: UserPermission[] = [];
  for (const entry of dto.permissions ?? []) {
    const mapped = mapPermission(entry);
    if (mapped !== null) permissions.push(mapped);
  }

  if (issues.length > 0) {
    logger.warn('me.schema.invalid', { issues });
  }

  return {
    id: guid,
    email,
    firstName,
    lastName,
    fullName: composeFullName(firstName, lastName),
    role,
    status: narrowStatus(raw.status),
    department: nullableString(dto.department),
    title: nullableString(dto.title),
    permissions,
    permissionKeys: buildPermissionKeys(permissions),
  };
}

function describeType(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string' && value.length === 0) return 'empty string';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

export function hasPermission(
  profile: UserProfile,
  resource: string,
  action: PermissionAction,
): boolean {
  return profile.permissionKeys.has(`${resource}:${action}`);
}
